import { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  FaStore, FaSignOutAlt, FaBars, FaTimes, 
  FaUser, FaChartBar, FaCog
} from 'react-icons/fa';
import { t, LanguageSwitcher } from '../../i18n'
import BrandLogo from '../BrandLogo'

export default function OwnerLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const navItems = [
    { 
      path: '/owner/dashboard', 
      label: 'Restaurants', 
      icon: FaStore 
    },
    ...(user?.isSuperAdmin ? [{ path: '/platform', label: 'Platform console', icon: FaChartBar }] : []),
  ];

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="flex h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900 overflow-hidden">
      {/* Sidebar */}
      <aside className={`
        fixed md:static inset-y-0 start-0 z-50 w-64 
        bg-gradient-to-b from-gray-900 to-gray-800 
        border-e border-purple-500/20 
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between p-6 border-b border-purple-500/20">
            <div className="flex items-center gap-3">
              <BrandLogo showName={false} imageClassName="h-11 w-11" />
              <div>
                <h1 className="text-xl font-bold text-white">{t("Rawaj Business")}</h1>
                <p className="text-xs text-purple-300">{t("Owner Portal")}</p>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden text-gray-400 hover:text-white transition-colors"
            >
              <FaTimes className="text-xl" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-xl transition-all
                    ${isActive(item.path)
                      ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white shadow-lg'
                      : 'text-gray-300 hover:bg-white/10 hover:text-white'
                    }
                  `}
                >
                  <Icon className="text-xl" />
                  <span className="font-medium">{t(item.label)}</span>
                </Link>
              );
            })}
          </nav>

          {/* User Info & Logout */}
          <div className="p-4 border-t border-purple-500/20">
            <div className="bg-white/5 rounded-xl p-4 mb-3">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-10 h-10 bg-gradient-to-br from-purple-600 to-blue-600 rounded-full flex items-center justify-center">
                  <FaUser className="text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{user?.full_name || user?.username}</p>
                  <p className="text-xs text-purple-300 capitalize">{t(user?.role)}</p>
                </div>
              </div>
            </div>
            <div className="flex justify-center mb-3 text-white"><LanguageSwitcher className="!bg-white/10 !border-white/20" /></div>
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600/20 hover:bg-red-600/30 text-red-400 hover:text-red-300 rounded-xl transition-all border border-red-500/30"
            >
              <FaSignOutAlt />
              <span>{t("Logout")}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Mobile Header */}
        <header className="md:hidden bg-gray-900 border-b border-purple-500/20 p-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-white hover:text-purple-400 transition-colors"
          >
            <FaBars className="text-xl" />
          </button>
          <div className="flex items-center gap-2">
            <BrandLogo showName={false} imageClassName="h-9 w-9" />
            <span className="text-white font-bold">{t("Rawaj Business Owner")}</span>
          </div>
          <div className="w-8"></div> {/* Spacer for centering */}
        </header>

        {/* Main Content Area */}
        <main className="flex-1 overflow-auto">
          <Outlet />
        </main>
      </div>

      {/* Backdrop for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden backdrop-blur-sm"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
}
